import React, { useState, useRef, useEffect } from 'react';
import { Plus, Monitor, HardDrive, Cloud, ArrowDownAZ } from 'lucide-react';
import { toast } from 'react-toastify';
import '../../styles/design-system.css';

/**
 * ToolActionButtons Component
 * Floating action buttons for quick actions in PDF tools
 * 
 * @param {Object} props
 * @param {Function} props.onAdd - Callback for add/create action
 * @param {Function} props.onDesktopView - Callback for desktop view toggle
 * @param {Function} props.onGoogleDrive - Callback for Google Drive integration
 * @param {Function} props.onDropbox - Callback for Dropbox integration
 * @param {Function} props.onSort - Callback for sort action
 * @param {number} props.notificationCount - Number to show in notification badge
 * @param {boolean} props.showDesktopView - Show desktop view button
 * @param {boolean} props.showCloudStorage - Show cloud storage buttons
 */
const ToolActionButtons = ({
    onAdd,
    onDesktopView,
    onGoogleDrive,
    onDropbox,
    onSort,
    notificationCount = 0,
    showDesktopView = true,
    showCloudStorage = true
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showSortMenu, setShowSortMenu] = useState(false);
    const sortMenuRef = useRef(null);

    // Close sort menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (sortMenuRef.current && !sortMenuRef.current.contains(event.target)) {
                setShowSortMenu(false);
            }
        };
        if (showSortMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showSortMenu]);

    const handleAdd = () => {
        if (onAdd) {
            onAdd();
        } else {
            toast.info('Add functionality coming soon');
        }
    };

    const handleDesktopView = () => {
        if (onDesktopView) {
            onDesktopView();
        } else {
            toast.info('Desktop view coming soon');
        }
    };

    const handleGoogleDrive = () => {
        if (onGoogleDrive) {
            onGoogleDrive();
        } else {
            toast.info('Google Drive integration coming soon');
        }
    };

    const handleDropbox = () => {
        if (onDropbox) {
            onDropbox();
        } else {
            toast.info('Dropbox integration coming soon');
        }
    };

    const handleSort = () => {
        setShowSortMenu(!showSortMenu);
    };

    const sortOptions = [
        { id: 'name-asc', label: 'Name (A-Z)', icon: '🔤' },
        { id: 'name-desc', label: 'Name (Z-A)', icon: '🔤' }
    ];

    return (
        <div
            style={{
                position: 'fixed',
                right: 'var(--spacing-6)',
                bottom: 'var(--spacing-6)',
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column-reverse',
                gap: 'var(--spacing-3)',
                alignItems: 'center'
            }}
        >
            {/* Main Add Button with Notification Badge */}
            <div style={{ position: 'relative' }}>
                <button
                    onClick={() => {
                        setIsExpanded(!isExpanded);
                        if (!isExpanded) {
                            handleAdd();
                        }
                    }}
                    style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '50%',
                        backgroundColor: '#E63946',
                        border: 'none',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(230, 57, 70, 0.4)',
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        zIndex: 10
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.1)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(230, 57, 70, 0.5)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(230, 57, 70, 0.4)';
                    }}
                >
                    <Plus size={24} strokeWidth={2.5} />
                </button>
                {notificationCount > 0 && (
                    <div
                        style={{
                            position: 'absolute',
                            top: '-4px',
                            left: '-4px',
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            backgroundColor: '#000000',
                            border: '2px solid #E63946',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            zIndex: 11
                        }}
                    >
                        {notificationCount > 9 ? '9+' : notificationCount}
                    </div>
                )}
            </div>

            {/* Expanded Action Buttons */}
            {isExpanded && (
                <>
                    {/* Desktop View Button */}
                    {showDesktopView && (
                        <button
                            onClick={handleDesktopView}
                            style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '50%',
                                backgroundColor: '#E63946',
                                border: 'none',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                transition: 'all 0.3s ease',
                                animation: 'slideInRight 0.3s ease-out'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.1)';
                                e.currentTarget.style.backgroundColor = '#d32f2f';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.backgroundColor = '#E63946';
                            }}
                        >
                            <Monitor size={20} strokeWidth={2.5} />
                        </button>
                    )}

                    {/* Google Drive Button */}
                    {showCloudStorage && (
                        <button
                            onClick={handleGoogleDrive}
                            style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '50%',
                                backgroundColor: '#E63946',
                                border: 'none',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                transition: 'all 0.3s ease',
                                animation: 'slideInRight 0.4s ease-out'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.1)';
                                e.currentTarget.style.backgroundColor = '#d32f2f';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.backgroundColor = '#E63946';
                            }}
                        >
                            {/* Google Drive Icon - simplified triangle */}
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M7.71 6.5L1.15 12l6.56 5.5-1.5 1.5-7.71-6.5L0 12l.5-.5 7.71-6.5 1.5 1.5zm6.58 0L12.15 12l2.14 5.5 7.71-6.5L22.85 12l-.5-.5-7.71-6.5-1.5 1.5z" />
                            </svg>
                        </button>
                    )}

                    {/* Dropbox Button */}
                    {showCloudStorage && (
                        <button
                            onClick={handleDropbox}
                            style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '50%',
                                backgroundColor: '#E63946',
                                border: 'none',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                transition: 'all 0.3s ease',
                                animation: 'slideInRight 0.5s ease-out'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.1)';
                                e.currentTarget.style.backgroundColor = '#d32f2f';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.backgroundColor = '#E63946';
                            }}
                        >
                            {/* Dropbox Icon - simplified box/diamond */}
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                            </svg>
                        </button>
                    )}

                    {/* Sort Button with Menu */}
                    <div style={{ position: 'relative' }} ref={sortMenuRef}>
                        <button
                            onClick={handleSort}
                            style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '50%',
                                backgroundColor: showSortMenu ? '#E63946' : '#ffffff',
                                border: `2px solid ${showSortMenu ? '#E63946' : '#e5e7eb'}`,
                                color: showSortMenu ? '#ffffff' : '#000000',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                transition: 'all 0.3s ease',
                                animation: 'slideInRight 0.6s ease-out'
                            }}
                            onMouseEnter={(e) => {
                                if (!showSortMenu) {
                                    e.currentTarget.style.transform = 'scale(1.1)';
                                    e.currentTarget.style.borderColor = '#E63946';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(230, 57, 70, 0.2)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!showSortMenu) {
                                    e.currentTarget.style.transform = 'scale(1)';
                                    e.currentTarget.style.borderColor = '#e5e7eb';
                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                                }
                            }}
                        >
                            <ArrowDownAZ size={20} strokeWidth={2.5} />
                        </button>
                        
                        {/* Sort Menu Dropdown */}
                        {showSortMenu && (
                            <div
                                style={{
                                    position: 'absolute',
                                    bottom: '60px',
                                    right: '0',
                                    backgroundColor: '#ffffff',
                                    borderRadius: '12px',
                                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                                    padding: '8px',
                                    minWidth: '200px',
                                    zIndex: 1001,
                                    animation: 'slideUp 0.3s ease-out'
                                }}
                            >
                                {sortOptions.map((option, index) => (
                                    <button
                                        key={option.id}
                                        onClick={() => {
                                            if (onSort) {
                                                onSort(option.id);
                                            }
                                            setShowSortMenu(false);
                                        }}
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            textAlign: 'left',
                                            border: 'none',
                                            backgroundColor: 'transparent',
                                            cursor: 'pointer',
                                            borderRadius: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            fontSize: '14px',
                                            color: '#1f2937',
                                            transition: 'all 0.2s ease',
                                            animation: `fadeIn 0.2s ease-out ${index * 0.05}s both`
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#f3f4f6';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                        }}
                                    >
                                        <span style={{ fontSize: '16px' }}>{option.icon}</span>
                                        <span>{option.label}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Animation Styles */}
            <style>{`
                @keyframes slideInRight {
                    from {
                        opacity: 0;
                        transform: translateX(20px) translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0) translateY(0);
                    }
                }
                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                    }
                    to {
                        opacity: 1;
                    }
                }
            `}</style>
        </div>
    );
};

export default ToolActionButtons;

